import type { ConsoleBrandDetail, ConsoleReview } from '@rsecure/brand-contracts/console';
import { Link } from 'react-router';
import { Badge, Json, StatusBadge, Time } from '../../components/ui';

export function ReviewCardBody({ r }: { r: ConsoleReview }) {
  const ev =
    (r.details.evidence as Array<{ check: string; outcome: string; detail: string }> | undefined) ??
    [];
  const provider = r.details.provider as
    | { id?: string; name?: string; domain?: string }
    | undefined;
  const curated = r.details.curated as { name?: string; domain?: string } | undefined;
  const proposed = r.details.proposedRelation as
    | { kind?: string; targetDomain?: string; note?: string }
    | undefined;
  return (
    <div className="grid" style={{ gap: 10 }}>
      <div>{r.summary}</div>
      {(curated || provider) && (
        <dl className="dl">
          {curated && (
            <>
              <dt>Curated identity</dt>
              <dd>
                {curated.name} · <bdi dir="ltr">{curated.domain}</bdi>
              </dd>
            </>
          )}
          {provider && (
            <>
              <dt>Provider claims</dt>
              <dd>
                {provider.name ?? '—'} · <bdi dir="ltr">{provider.domain ?? '—'}</bdi>{' '}
                <span className="faint mono">{provider.id}</span>
              </dd>
            </>
          )}
          {proposed?.kind && (
            <>
              <dt>Proposed relation</dt>
              <dd>
                {proposed.kind} → <bdi dir="ltr">{proposed.targetDomain}</bdi>
                {proposed.note && <div className="muted">{proposed.note}</div>}
              </dd>
            </>
          )}
        </dl>
      )}
      {ev.length > 0 && (
        <ul className="evidence">
          {ev.map((e) => (
            <li key={e.check + e.detail}>
              <Badge tone={e.outcome === 'pass' ? 'ok' : e.outcome === 'warn' ? 'warn' : 'bad'}>
                {e.check}: {e.outcome}
              </Badge>
              <span>{e.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {r.resolvedBy && (
        <div className="muted">
          Resolved {r.status.toLowerCase()} by <span className="mono">{r.resolvedBy}</span> ·{' '}
          <Time iso={r.resolvedAt} />
        </div>
      )}
      <Json value={r.details} summary="Persisted evidence (details)" />
    </div>
  );
}

export function BrandReviewsTab({ d }: { d: ConsoleBrandDetail }) {
  if (!d.reviews.length)
    return <p className="muted">No reviews have been recorded for this entity.</p>;
  return (
    <div className="grid" style={{ gap: 12 }}>
      {d.reviews.map((r) => (
        <section key={r.id} className="card" data-testid="brand-review">
          <div className="card-h">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusBadge
                kind="review"
                value={r.status}
                tone={r.status === 'OPEN' ? (r.blocking ? 'bad' : 'warn') : undefined}
              />
              <strong className="mono">{r.kind}</strong>
              <Badge>{r.class.replace(/_/g, ' ')}</Badge>
              {r.blocking && <Badge tone="bad">blocks publication</Badge>}
            </div>
            <span className="muted" style={{ fontSize: 12 }}>
              opened <Time iso={r.createdAt} relative /> ·{' '}
              <Link className="link" to={`/reviews/${r.id}`}>
                open in queue
              </Link>
            </span>
          </div>
          <div className="card-b">
            <ReviewCardBody r={r} />
          </div>
        </section>
      ))}
    </div>
  );
}
